import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DetailPageLayout from '@/components/layout/DetailPageLayout';
import Breadcrumb from '@/components/common/Breadcrumb';
import ConfirmModal from '@/components/common/ConfirmModal';
import MoreActionMenu from '@/components/common/MoreActionMenu';
import CommentSection from '@/components/feature/CommentSection';
import PostModal from '../components/PostModal';
import ReportModal from '../components/ReportModal';
import { useAuth } from '@/hooks/useAuth';
import { useCommunityTopicStore } from '@/stores/communityTopicStore';
import { useAssetRenewStore } from '@/stores';
import {
  fetchCommunityPostDetail,
  fetchCommunityPosts,
  likeCommunityPost,
  unlikeCommunityPost,
  deleteCommunityPost,
  shareCommunityPost,
} from '@/service/api/community';

const inFlightRequests = new Map<string, Promise<Api.Community.PostResponse>>();

function getPostDetailDeduplicated(id: string): Promise<Api.Community.PostResponse> {
  if (inFlightRequests.has(id)) {
    return inFlightRequests.get(id)!;
  }
  const promise = fetchCommunityPostDetail(id).finally(() => {
    inFlightRequests.delete(id);
  });
  inFlightRequests.set(id, promise);
  return promise;
}

export default function CommunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();

  const [post, setPost] = useState<Api.Community.PostResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [likeLoading, setLikeLoading] = useState<boolean>(false);
  const [editPostModalOpen, setEditPostModalOpen] = useState<boolean>(false);
  const [reportModalOpen, setReportModalOpen] = useState<boolean>(false);

  const [relatedPosts, setRelatedPosts] = useState<Api.Community.PostResponse[]>([]);
  const [relatedLoading, setRelatedLoading] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2500);
  };

  const { topics: sidebarTopics, fetchTopics: fetchSidebarTopics } = useCommunityTopicStore();

  useEffect(() => {
    fetchSidebarTopics().catch(() => { });
  }, [fetchSidebarTopics]);

  useEffect(() => {
    if (!post || !post.id) return;
    let cancelled = false;

    const topicIds = (post.topics || []).map((t) => t.id).filter(Boolean) as string[];

    if (topicIds.length > 0) {
      setRelatedLoading(true);
      fetchCommunityPosts({
        topic_ids: topicIds,
        page: 1,
        page_size: 10,
      })
        .then((res) => {
          if (!cancelled) {
            const items = (res?.items || []).filter((p) => p.id !== post.id).slice(0, 3);
            setRelatedPosts(items);
          }
        })
        .catch((err) => {
          console.error('根据 topic 获取相关帖子失败:', err);
          if (!cancelled) {
            setRelatedPosts([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setRelatedLoading(false);
          }
        });
    } else {
      setRelatedPosts([]);
    }

    return () => {
      cancelled = true;
    };
  }, [post?.id, post?.topics]);

  const currentUserId = user?.id || (user as unknown as { account_id?: string })?.account_id;
  const isPostOwner = Boolean(
    isLoggedIn &&
    post &&
    currentUserId &&
    post.author_account_id &&
    currentUserId === post.author_account_id
  );

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getPostDetailDeduplicated(id)
      .then((res) => {
        if (!cancelled && res) {
          setPost(res);
        }
      })
      .catch((err) => {
        console.error('获取社区动态详情失败:', err);
        if (!cancelled) {
          setPost(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const assetCache = useAssetRenewStore((state) => state.assetCache);

  // 离场/路由切换时自动清空临时续期缓存
  useEffect(() => {
    return () => {
      useAssetRenewStore.getState().clearCache();
    };
  }, [id]);

  // 监听 post 变更，自动收集帖子配图与视频资产 ID 并进行凭证注册与按需续签
  useEffect(() => {
    if (!post || !id) return;

    // 1. 优先集中将原接口返回的媒体资产与作者头像的初始 access_url 注册至 Store 缓存
    const mediaAssets = post.media_assets || [];
    mediaAssets.forEach((m) => {
      const assetId = m.asset_id;
      const url = m.access_url || m.source_access_url || m.hls_manifest_url || (m as any).url;
      const expires = m.access_expires || m.source_access_expires_at || (m as any).expires_at;
      if (assetId && url) {
        useAssetRenewStore.getState().setAssetCacheItem(String(assetId), url, expires);
      }
    });

    const avatarAssetId = post.author_avatar_asset_id;
    const avatarUrl = post.author_avatar_access_url || post.author_avatar?.access_url;
    const avatarExpires = post.author_avatar?.access_expires;
    if (avatarAssetId && avatarUrl) {
      useAssetRenewStore.getState().setAssetCacheItem(String(avatarAssetId), avatarUrl, avatarExpires);
    }

    const renewRequests: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];
    const singleAssetIds: string[] = [];

    const mediaAssetIds = mediaAssets
      .map((m) => m.asset_id)
      .filter(Boolean) as string[];

    // 社区帖子媒体资源统一使用 COMMUNITY_POST 资源类型
    if (id && mediaAssetIds.length) {
      renewRequests.push({
        resource_type: 'COMMUNITY_POST',
        resource_id: id,
        asset_ids: mediaAssetIds,
      });
    }

    // 作者头像：直接使用 post.author_avatar_asset_id 与 post.author_account_id 进行 ACCOUNT 资源类型续期注册
    if (avatarAssetId && post.author_account_id) {
      renewRequests.push({
        resource_type: 'ACCOUNT',
        resource_id: String(post.author_account_id),
        asset_ids: [String(avatarAssetId)],
      });
    } else if (avatarAssetId) {
      singleAssetIds.push(String(avatarAssetId));
    }

    if (renewRequests.length) {
      useAssetRenewStore.getState().renewBatchResources(renewRequests).catch(() => { });
    }

    singleAssetIds.forEach((assetId) => {
      useAssetRenewStore.getState().renewSingleAsset(assetId).catch(() => { });
    });
  }, [post, id]);

  // 处理统一点赞/取消点赞
  const handleToggleLike = async () => {
    if (!post || !post.id || likeLoading) return;

    const currentLiked = post.is_liked ?? false;
    const currentLikes = post.like_count ?? 0;
    const nextLiked = !currentLiked;
    const nextLikes = nextLiked ? currentLikes + 1 : Math.max(0, currentLikes - 1);

    setPost((prev) =>
      prev
        ? {
          ...prev,
          is_liked: nextLiked,
          like_count: nextLikes,
        }
        : null
    );

    setLikeLoading(true);

    try {
      const res = nextLiked ? await likeCommunityPost(post.id) : await unlikeCommunityPost(post.id);
      if (res) {
        setPost((prev) =>
          prev
            ? {
              ...prev,
              is_liked: res.liked ?? nextLiked,
              like_count: res.like_count ?? nextLikes,
            }
            : null
        );
      }
    } catch (err) {
      console.error('点赞/取消点赞操作失败:', err);
      setPost((prev) =>
        prev
          ? {
            ...prev,
            is_liked: currentLiked,
            like_count: currentLikes,
          }
          : null
      );
    } finally {
      setLikeLoading(false);
    }
  };

  // 处理分享动态 (复制【标题】链接到剪贴板并调用接口)
  const handleShare = async () => {
    if (!post || !post.id) return;

    const title = post.title?.trim() || '社区动态';
    const shareUrl = `${window.location.origin}/community/${post.id}`;
    const textToCopy = `【${title}】${shareUrl}`;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      showToast('已复制分享链接到剪贴板');
    } catch (err) {
      console.error('复制到剪贴板失败:', err);
      showToast('复制失败，请手动复制链接');
    }

    try {
      const res = await shareCommunityPost(post.id, { share_channel: 'LINK' });
      if (res) {
        setPost((prev) =>
          prev
            ? {
              ...prev,
              share_count: res.share_count ?? (prev.share_count || 0) + 1,
            }
            : null
        );
      }
    } catch (err) {
      console.error('记录动态分享接口失败:', err);
    }
  };

  const [confirmDeletePost, setConfirmDeletePost] = useState(false);
  const [deletingPostLoading, setDeletingPostLoading] = useState(false);

  const handleOpenDeletePostModal = () => {
    if (!post || !post.id) return;
    setConfirmDeletePost(true);
  };

  const handleExecuteDeletePost = async () => {
    if (!post || !post.id || deletingPostLoading) return;
    setDeletingPostLoading(true);
    try {
      await deleteCommunityPost(post.id);
      navigate('/community');
    } catch (err) {
      console.error('删除动态失败:', err);
      alert('删除失败，请稍后重试');
    } finally {
      setDeletingPostLoading(false);
      setConfirmDeletePost(false);
    }
  };

  if (loading) {
    return (
      <DetailPageLayout bgClass="bg-background-50">
        <div className="pt-20 sm:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3 text-slate-400">
            <i className="ri-loader-4-line text-3xl animate-spin text-slate-600"></i>
            <span className="text-sm font-medium">正在加载动态详情...</span>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!post) {
    return (
      <DetailPageLayout bgClass="bg-background-50">
        <div className="pt-20 sm:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-slate-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">帖子未找到</h2>
            <p className="text-sm text-slate-500 mb-4">该帖子可能已被删除或不存在</p>
            <Link to="/community" className="text-sm text-slate-800 hover:text-slate-950 cursor-pointer font-semibold underline underline-offset-4">
              返回社区广场
            </Link>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  const formatNumber = (n: number): string => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
  };

  const formatTime = (iso?: string): string => {
    if (!iso) return '未知时间';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return '刚刚';
    if (mins < 60) return `${mins} 分钟前`;
    if (hrs < 24) return `${hrs} 小时前`;
    if (days < 7) return `${days} 天前`;
    return new Date(iso).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  };

  const authorName = post.author_display_name || '社区用户';
  const authorAvatarAssetId = post.author_avatar_asset_id;

  const authorAvatarUrl =
    (authorAvatarAssetId && assetCache[authorAvatarAssetId]?.url) ||
    post.author_avatar_access_url ||
    post.author_avatar?.access_url;
  const topics = post.topics || [];
  const mediaAssets = post.media_assets || [];
  const validMedia = mediaAssets.filter((m) => Boolean(m.access_url || m.source_access_url || m.hls_manifest_url));

  const getMediaUrl = (m: Api.Community.MediaAssetResponse) => {
    const assetId = m.asset_id;
    if (assetId && assetCache[assetId]?.url) {
      return assetCache[assetId].url;
    }
    return m.access_url || m.source_access_url || m.hls_manifest_url || '';
  };

  const isVideoMedia = (m: Api.Community.MediaAssetResponse) =>
    m.media_type?.toUpperCase() === 'VIDEO';

  return (
    <DetailPageLayout
      bgClass="bg-background-50"
      sidebarWidth="lg:w-[340px]"
      header={
        <div className="pt-16 sm:pt-20">
          <div className="detail-shell">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '社区广场', href: '/community' },
                { label: post.title || '动态详情' },
              ]}
            />
          </div>
        </div>
      }
      sidebar={
        <>
          {/* Post Stats */}
          <div className="hidden lg:block bg-white rounded-2xl p-5 border border-background-200/70">
            <h3 className="font-semibold text-foreground-950 text-sm mb-4">帖子数据</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-xl bg-slate-50/70 border border-slate-100">
                <div className="font-heading font-bold text-lg text-slate-900 flex items-center justify-center gap-1">
                  <i className="ri-heart-line text-rose-500 mr-0.5"></i>
                  {formatNumber(post.like_count ?? 0)}
                </div>
                <div className="text-xs text-foreground-400 mt-1">点赞</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-slate-50/70 border border-slate-100">
                <div className="font-heading font-bold text-lg text-slate-900">
                  {post.comment_count ?? 0}
                </div>
                <div className="text-xs text-foreground-400 mt-1">评论</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-slate-50/70 border border-slate-100">
                <div className="font-heading font-bold text-lg text-slate-900">
                  {formatNumber(post.share_count ?? 0)}
                </div>
                <div className="text-xs text-foreground-400 mt-1">分享</div>
              </div>
            </div>
          </div>

          {/* Trending Topics */}
          <div className="hidden lg:block bg-white rounded-2xl p-5 border border-background-200/70">
            <h3 className="font-semibold text-foreground-950 text-sm mb-4 flex items-center gap-2">
              <i className="ri-fire-line text-amber-500 text-base"></i>
              热门话题
            </h3>
            <div className="space-y-1">
              {sidebarTopics.slice(0, 6).map((topic) => (
                <Link
                  key={topic.id || topic.name}
                  to={`/community?topic_ids=${topic.id}`}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <span className="text-sm text-foreground-700 font-medium">#{topic.name}</span>
                  <span className="text-xs text-foreground-400">
                    {topic.post_count ? `${formatNumber(topic.post_count)} 讨论` : '讨论'}
                  </span>
                </Link>
              ))}
              {sidebarTopics.length === 0 && (
                <div className="text-xs text-foreground-400 py-2 text-center">暂无热门话题</div>
              )}
            </div>
          </div>
        </>
      }
      extra={
        (relatedLoading || relatedPosts.length > 0) ? (
          <section className="pt-2 pb-4">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center font-bold">
                  <i className="ri-article-line text-lg"></i>
                </div>
                <div>
                  <h2 className="font-heading font-bold text-base text-foreground-950">相关帖子</h2>
                  <p className="text-xs text-foreground-400">基于热门讨论与相关标签推荐的动态内容</p>
                </div>
              </div>
              {!relatedLoading && relatedPosts.length > 0 && (
                <span className="text-xs text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full font-medium border border-slate-200/60">
                  {relatedPosts.length} 篇相关
                </span>
              )}
            </div>

            {relatedLoading ? (
              <div className="flex items-center justify-center py-12 text-foreground-400 gap-2.5 text-xs bg-white rounded-xl border border-background-200/70">
                <i className="ri-loader-4-line text-lg animate-spin text-slate-600"></i>
                <span>正在加载相关帖子...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedPosts.map((rp) => {
                  const likes = rp.like_count ?? (rp as unknown as { likes?: number }).likes ?? 0;
                  const comments = rp.comment_count ?? (rp as unknown as { commentCount?: number }).commentCount ?? 0;

                  return (
                    <Link
                      key={rp.id}
                      to={`/community/${rp.id}`}
                      className="bg-white rounded-xl p-5 border border-background-200/70 hover:border-slate-300 hover:shadow-md transition-all duration-200 cursor-pointer group flex flex-col justify-between"
                    >
                      <div>
                        <h4 className="font-semibold text-sm text-foreground-950 group-hover:text-slate-900 transition-colors line-clamp-2 leading-relaxed mb-3">
                          {rp.title || '无标题动态'}
                        </h4>
                      </div>

                      <div className="pt-3 border-t border-gray-50 flex items-center justify-between text-xs text-foreground-400">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1 group-hover:text-rose-500 transition-colors">
                            <i className="ri-heart-3-line"></i>
                            {formatNumber(likes)}
                          </span>
                          <span className="flex items-center gap-1 group-hover:text-slate-700 transition-colors">
                            <i className="ri-chat-3-line"></i>
                            {formatNumber(comments)}
                          </span>
                        </div>

                        <span className="flex items-center gap-1 group-hover:text-slate-800 transition-colors font-medium text-[11px]">
                          阅读全文
                          <i className="ri-arrow-right-line transform group-hover:translate-x-1 transition-transform"></i>
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        ) : null
      }
      comments={
        post.id ? (
          <CommentSection
            targetType="COMMUNITY_POST"
            targetId={post.id}
            initialCommentCount={post.comment_count ?? 0}
            isTargetAuthor={isPostOwner}
            onPreviewImage={(url) => setPreviewImageUrl(url)}
            onCommentCountChange={(count) =>
              setPost((prev) => (prev ? { ...prev, comment_count: count } : null))
            }
          />
        ) : null
      }
      modals={
        <>
          <ConfirmModal
            open={confirmDeletePost}
            title="删除动态"
            content="确定要删除这条动态吗？此操作无法撤销。"
            type="danger"
            confirmText="确定删除"
            cancelText="取消"
            loading={deletingPostLoading}
            onConfirm={handleExecuteDeletePost}
            onCancel={() => setConfirmDeletePost(false)}
          />

          {previewImageUrl && (
            <div
              className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
              onClick={() => setPreviewImageUrl(null)}
            >
              <div className="relative max-w-5xl max-h-[90vh] flex items-center justify-center">
                <img
                  src={previewImageUrl}
                  alt="大图预览"
                  className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  onClick={() => setPreviewImageUrl(null)}
                  className="absolute -top-10 right-0 text-white text-3xl hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
          )}

          <PostModal
            open={editPostModalOpen}
            initialData={post}
            onClose={() => setEditPostModalOpen(false)}
            onSuccess={() => {
              if (post?.id) {
                getPostDetailDeduplicated(post.id).then((res) => {
                  if (res) setPost(res);
                });
              }
            }}
          />

          <ReportModal
            open={reportModalOpen}
            postId={post?.id}
            postTitle={post?.title}
            onClose={() => setReportModalOpen(false)}
            onSuccess={() => showToast('举报已有序提交，我们会尽快审核处理')}
          />

          {toastMsg && (
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl bg-foreground-950/90 text-white text-sm font-medium shadow-lg backdrop-blur-md flex items-center gap-2 animate-fade-in">
              <i className="ri-checkbox-circle-fill text-sky-400 text-base"></i>
              <span>{toastMsg}</span>
            </div>
          )}
        </>
      }
    >
      {/* Article Header */}
      <article className="bg-white rounded-2xl p-6 md:p-8 border border-background-200/70">
        {/* Author Row */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            {authorAvatarUrl ? (
              <img
                src={authorAvatarUrl}
                alt={authorName}
                className="w-11 h-11 rounded-full object-cover flex-shrink-0 border border-slate-100"
                loading="lazy"
              />
            ) : (
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white font-bold flex items-center justify-center text-lg flex-shrink-0 select-none shadow-xs">
                {authorName.trim().slice(0, 1).toUpperCase() || '用'}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground-950">{authorName}</span>
                {isPostOwner && (
                  <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium border border-slate-200/60">
                    作者 (我)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-400">
                <span>{formatTime(post.published_at || post.created_at)}</span>
              </div>
            </div>
          </div>

          <MoreActionMenu
            align="right"
            items={[
              ...(isPostOwner
                ? [
                  {
                    key: 'edit-post',
                    label: '编辑动态',
                    icon: 'ri-edit-line',
                    onClick: () => setEditPostModalOpen(true),
                  },
                  {
                    key: 'delete-post',
                    label: '删除动态',
                    icon: 'ri-delete-bin-line',
                    danger: true,
                    onClick: handleOpenDeletePostModal,
                  },
                ]
                : []),
              {
                key: 'report-post',
                label: '举报动态',
                icon: 'ri-flag-line',
                danger: true,
                onClick: () => setReportModalOpen(true),
              },
            ]}
          />
        </div>

        {/* Title */}
        <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
          {post.title}
        </h1>

        {/* Tags/Topics */}
        {topics.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {topics.map((topic) => (
              <Link
                key={topic.id || topic.name}
                to={`/community?tag=${topic.name}`}
                className="text-xs px-3 py-1 rounded-full bg-slate-100/90 text-slate-600 font-medium hover:bg-slate-200/90 hover:text-slate-900 border border-slate-200/50 transition-colors cursor-pointer"
              >
                #{topic.name}
              </Link>
            ))}
          </div>
        )}

        {/* Media */}
        {validMedia.length === 1 && (
          <div className="mb-6 w-full max-w-2xl">
            <div className="relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-200/80 shadow-2xs w-full h-[260px] sm:h-[320px] md:h-[380px] flex items-center justify-center">
              {isVideoMedia(validMedia[0]) ? (
                validMedia[0].video_status === 'PROCESSING' || validMedia[0].video_status === 'TRANSCODING' ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-slate-900 text-white p-4">
                    <div className="w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mb-2"></div>
                    <span className="text-xs text-gray-200 font-medium">视频正在后台转码中...</span>
                    <span className="text-[11px] text-gray-400 mt-1">处理完成后刷新即可直接播放</span>
                  </div>
                ) : (
                  <video
                    src={getMediaUrl(validMedia[0])}
                    controls
                    preload="metadata"
                    className="w-full h-full object-contain bg-black"
                  />
                )
              ) : (
                <img
                  src={getMediaUrl(validMedia[0])}
                  alt={post.title || '动态媒体'}
                  className="w-full h-full object-contain cursor-pointer hover:opacity-95 transition-opacity"
                  loading="lazy"
                  onClick={() => setPreviewImageUrl(getMediaUrl(validMedia[0]))}
                />
              )}
            </div>
          </div>
        )}

        {validMedia.length > 1 && (
          <div className={`grid gap-3 mb-6 ${validMedia.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
            {validMedia.map((mediaItem, idx) => {
              const url = getMediaUrl(mediaItem);
              const isVid = isVideoMedia(mediaItem);
              return (
                <div key={mediaItem.asset_id || idx} className="relative rounded-xl overflow-hidden border border-gray-100/90 bg-gray-50 aspect-square flex items-center justify-center group/media">
                  {isVid ? (
                    mediaItem.video_status === 'PROCESSING' || mediaItem.video_status === 'TRANSCODING' ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white p-2 text-center">
                        <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mb-1.5"></div>
                        <span className="text-xs text-gray-300">转码中...</span>
                      </div>
                    ) : (
                      <video
                        src={url}
                        controls
                        preload="metadata"
                        className="w-full h-full object-contain bg-black rounded-xl"
                      />
                    )
                  ) : (
                    <img
                      src={url}
                      alt={post.title || `动态媒体 ${idx + 1}`}
                      className="w-full h-full object-cover rounded-xl cursor-pointer group-hover/media:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onClick={() => setPreviewImageUrl(url)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Full Description / Content */}
        <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed whitespace-pre-line">
          {post.content}
        </div>

        {/* Action Bar */}
        <ActionBar
          post={post}
          formatNumber={formatNumber}
          onToggleLike={handleToggleLike}
          likeLoading={likeLoading}
          onShare={handleShare}
        />
      </article>
    </DetailPageLayout>
  );
}

function ActionBar({
  post,
  formatNumber,
  onToggleLike,
  likeLoading,
  onShare,
}: {
  post: Api.Community.PostResponse;
  formatNumber: (n: number) => string;
  onToggleLike: () => void;
  likeLoading: boolean;
  onShare: () => void;
}) {
  const liked = post.is_liked ?? false;
  const localLikes = post.like_count ?? 0;
  const shareCount = post.share_count ?? 0;

  return (
    <div className="flex items-center gap-6 mt-8 pt-5 border-t border-background-200/70">
      <button
        onClick={onToggleLike}
        disabled={likeLoading}
        className={`flex items-center gap-2 text-sm transition-colors cursor-pointer ${liked ? 'text-rose-500 font-semibold' : 'text-slate-500 hover:text-rose-500'
          }`}
      >
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${liked ? 'bg-rose-50 text-rose-500' : 'bg-slate-100 hover:bg-rose-50 hover:text-rose-500'
            }`}
        >
          <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
        </div>
        <span className="text-sm font-medium">{formatNumber(localLikes)}</span>
      </button>

      <span className="flex items-center gap-2 text-sm text-slate-500">
        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
          <i className="ri-chat-1-line text-lg"></i>
        </div>
        <span className="text-sm font-medium">{post.comment_count ?? 0}</span>
      </span>

      <button className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors ml-auto cursor-pointer">
        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors">
          <i className="ri-bookmark-line text-lg"></i>
        </div>
      </button>

      <button
        onClick={onShare}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
        title="分享动态"
      >
        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors">
          <i className="ri-share-forward-line text-lg"></i>
        </div>
        <span className="text-sm font-medium">{formatNumber(shareCount)}</span>
      </button>
    </div>
  );
}
